/**
 * Projektzeilen des Anlagedialogs — die Rechnung ohne React.
 *
 * Aus InvoicesView herausgeloest: offene Positionen je Projekt, offene
 * Betraege je Monat, Protokolluecke, Deckung der Altfakturierung, Zustand der
 * Abrechnungsart, Suche und Summe. Das ist die schwerste Rechnung des
 * Anlagedialogs, und sie hatte keinen Test: die Ansicht ist ohne
 * DOM-Testumgebung nicht pruefbar (im Projekt gibt es keine). Die Ansicht
 * haelt nur noch die Memos samt Abhaengigkeiten und ruft von hier.
 *
 * Die Eingaben heissen bewusst wie die Werte der Ansicht (projectRows,
 * pickerInvoices, billableProjects ...): die Kommentare sind mit dem Code
 * umgezogen und sprechen von genau diesen Namen.
 */
import type { ExternalAdvance, Invoice } from '../../types';
import {
    computeOpenPositions,
    cumulativeDeliveryStart,
    openByMonth,
    undatedBacklog,
    undatedOpenSummary,
    type OpenMonthBucket,
    type OpenPosition,
    type UncoveredPriorBilling,
    type UndatedBacklogItem,
    type UndatedSummary,
} from '../../services/invoiceLogic';
import { invoicedQuantityByService } from '../../services/advanceInvoiceLogic';
import { altCoveredQuantities, backfillEntryIds } from '../../services/externalAdvances';
import {
    billingStancesByProject,
    consumesBillingMode,
    projectUncoveredPriorBilling,
    type BillingMode,
    type BillingStance,
} from '../../services/billingMode';
import { getNormalizedServices, isChildProject, type ProjectRowLike } from '../../services/projectLv';
import { contractedQuantity } from '../../services/nepPosition';
import { suggestFlatMonths, type FlatMonthSuggestion } from '../../services/flatRateBooking';
import { getServiceLog } from '../../services/serviceJournal';
import { roundCents, type Cents } from '../../services/money';
import type { CreatableDocumentType } from './invoicesViewShared';

// ─── Projektzeilen ──────────────────────────────────────────────────────────

/**
 * Was die Rechnung an einem Projekt liest.
 *
 * Die Ansicht haelt die Projekte untypisiert (loadProjectsFromCloud). Die
 * Funktionen hier sind deshalb generisch ueber das Projekt und reichen es
 * unveraendert in die Zeile durch: Liste und Rueckfrage-Kette lesen an ihm
 * weitere Felder (Kunde, Abschlussstand), die fuer die Rechnung keine Rolle
 * spielen.
 *
 * Inzwischen liefert loadProjectsFromCloud `ListProject[]`; in der Ansicht ist
 * P damit `ListProject`, also ein Listenauszug. Generisch bleibt es trotzdem:
 * die Tests rechnen mit Teilobjekten, und gelesen wird hier nur, was der
 * Auszug auch hat (Metadaten und eingebettete project_services).
 */
export type PickerProjectLike = ProjectRowLike & { id: string };

/**
 * Eine Projektzeile des Anlagedialogs — projectRows in InvoicesView.
 *
 * Was die Felder bedeuten und warum sie so gerechnet werden, steht an ihrer
 * Berechnung in buildPickerProjectRows.
 */
export interface PickerProjectRow<P extends PickerProjectLike = PickerProjectLike> {
    flatSuggestions: FlatMonthSuggestion[];
    backlog: UndatedBacklogItem[];
    backlogNetCents: Cents;
    backlogOpenNetCents: Cents;
    /** Erster protokollierter Tag bzw. Projektstart; '' heisst: kein Tag aus den Daten. */
    backlogSuggestedDate: string;
    undated: UndatedSummary;
    months: OpenMonthBucket[];
    project: P;
    positions: OpenPosition[];
    netCents: Cents;
    contractedNetCents: Cents;
}

/** Was die Ansicht fuer die Projektzeilen beisteuert — alles aus ihrem Zustand. */
export interface PickerProjectRowsInput<P extends PickerProjectLike> {
    /** Der geladene Projektbestand, ungefiltert. */
    projects: readonly P[];
    /** Ist der Anlagedialog offen? */
    pickerOpen: boolean;
    /** Belege MIT Positionen; null heisst: noch nicht geladen. */
    pickerInvoices: readonly Invoice[] | null;
    /** Zeitraum des Anlagedialogs (yyyy-mm-dd); leer heisst: offen. */
    periodFrom: string;
    periodTo: string;
    includeUndated: boolean;
    /** Heutiger Tag (ISO), hereingereicht statt new Date() — testbar. */
    today: string;
}

/**
 * Die Projektzeilen des Anlagedialogs, nach offenem Betrag absteigend.
 *
 * Kind-Projekte fehlen: ihr LV liegt am Elternprojekt (isChildProject), und
 * abgerechnet wird dort.
 */
export const buildPickerProjectRows = <P extends PickerProjectLike>({
    projects,
    pickerOpen,
    pickerInvoices,
    periodFrom,
    periodTo,
    includeUndated,
    today,
}: PickerProjectRowsInput<P>): PickerProjectRow<P>[] => {
    // Solange die Belegpositionen fehlen, gibt es keine Zeile. Mit einer
    // leeren Belegliste zu rechnen hiesse, jede bereits abgerechnete
    // Menge erneut als offen anzubieten — lieber nichts anzeigen als
    // eine zu hohe Zahl.
    //
    // Zusaetzlich am geoeffneten Dialog: der Bestand bleibt nach dem
    // Schliessen stehen (damit ein zweites Oeffnen ohne neue Abfrage
    // auskommt), und ohne diese Bedingung liefe die teure Rechnung —
    // computeOpenPositions und openByMonth ueber JEDES Projekt — bei
    // jeder Aenderung an Projekten oder Zeitraum weiter, obwohl der
    // Dialog zu ist. Gelesen wird das Ergebnis ausschliesslich in ihm.
    if (!pickerOpen || pickerInvoices === null) return [];
    return projects
        .filter((p) => !isChildProject(p))
        .map((p): PickerProjectRow<P> => {
            const services = getNormalizedServices(p);
            // Vorschau mit demselben Zeitraumfilter, den das Anlegen
            // anwendet. Ohne ihn zeigte die Liste den gesamten offenen
            // Bestand, und der Beleg fiele danach unerwartet klein aus.
            const serviceLog = getServiceLog(p.data);
            // Die Belege dieses Projekts tragen die festgeschriebene
            // Zuordnung zu den Protokolleintraegen. Damit zaehlt im
            // Zeitraum nur, was davon noch offen ist.
            const projectInvoices = (pickerInvoices ?? []).filter(
                (i) => i.primaryProjectId === p.id,
            );
            const positions = computeOpenPositions(services, new Map(), {
                from: periodFrom || undefined,
                to: periodTo || undefined,
                serviceLog,
                includeUndated,
                invoices: projectInvoices,
            });
            // Die GANZE Protokolluecke, nicht nur ihr offener Teil.
            // `undated` deckelt auf die offene Menge — richtig fuer die
            // Frage "was koennte ich hier abrechnen", falsch fuer die
            // Frage "was fehlt im Protokoll". Kumulativ ist "nichts
            // offen" der Normalfall, und genau dort blockiert die
            // Luecke trotzdem: die Grundlage misst gegen quantityUsed.
            const backlog = undatedBacklog(services, serviceLog);
            return {
                // MONATSPAUSCHALE: offene, noch nicht gebuchte Monate
                // (flatRateBooking). Ein ungebuchter Monat sperrt die
                // Rechnung spaeter ohnehin (flat_month_unbooked) —
                // hier steht der Ausweg VOR dem Anlegen, als eigener
                // Knopf neben der Zeile. Gebucht wird nur im Dialog.
                flatSuggestions: suggestFlatMonths({
                    services,
                    serviceLog,
                    startDate: p.data?.metadata?.globalData?.startDate,
                    endDate: p.data?.metadata?.globalData?.endDate,
                    today,
                }),
                backlog,
                backlogNetCents: backlog.reduce((sum, b) => sum + b.netCents, 0),
                // Getrennt ausgewiesen, weil es zwei verschiedene Lagen
                // sind: offenes Geld muss auf eine Rechnung, bereits
                // Abgerechnetes braucht nur sein Datum.
                backlogOpenNetCents: backlog.reduce((sum, b) => sum + b.openNetCents, 0),
                // Der Vorschlag fuer das Leistungsdatum: der erste Tag,
                // an dem in diesem Projekt ueberhaupt etwas
                // protokolliert wurde. Pauschalen aus dem Projektstart
                // — Vorankuendigung, SiGe-Plan, Baustellenordnung —
                // gehoeren genau dorthin, und kumulativ zaehlt jeder
                // spaetere Stichtag sie mit. Das Zeitraumende als
                // Vorgabe behauptete dagegen Leistung im laufenden
                // Monat, die Monate vorher stattfand.
                backlogSuggestedDate:
                    cumulativeDeliveryStart(serviceLog) ||
                    p.data?.metadata?.globalData?.startDate ||
                    '',
                undated: undatedOpenSummary(services, new Map(), serviceLog),
                // Bewusst OHNE Zeitraumfilter: die Uebersicht soll ja
                // gerade zeigen, was ausserhalb der Auswahl liegt.
                // Mengen, die eine Alt-Rechnung bereits abgegolten hat,
                // gehoeren nicht in die Uebersicht der offenen Monate.
                // Zwei Wege dorthin, und beide werden gebraucht:
                // altCoveredQuantities deckt den Altbestand ab, dessen
                // quantityBilled noch 0 ist (nachgetragen vor dieser
                // Aenderung); backfillEntryIds haelt die ALT-Zeilen
                // selbst aus der Monatsverteilung heraus, damit der
                // Altbestand nicht den falschen Monaten zugeschrieben
                // wird. NUR die Uebersicht — die Positionen des Belegs
                // (computeOpenPositions oben) sehen beides nicht, dort
                // haengt die Bemessungsgrundlage des Abschlagsabzugs dran.
                months: openByMonth(
                    services,
                    serviceLog,
                    new Map(),
                    projectInvoices,
                    altCoveredQuantities(serviceLog),
                    backfillEntryIds(serviceLog),
                ),
                project: p,
                positions,
                netCents: positions.reduce((s, pos) => s + pos.netCents, 0),
                // Auftragswert als Bemessungsgrundlage des Abschlags:
                // ein Abschlag bezieht sich auf den Gesamtauftrag, nicht
                // auf das bisher Erbrachte.
                contractedNetCents: services.reduce(
                    (sum, svc) =>
                        sum +
                        roundCents(
                            // NEP zaehlt erst, was gebucht ist.
                            contractedQuantity(svc) *
                                roundCents((svc.unitPrice || 0) * 100),
                        ),
                    0,
                ),
            };
        })
        .sort((a, b) => b.netCents - a.netCents);
};

// ─── Deckung der Altfakturierung und Zustand der Abrechnungsart ─────────────

/** Was die Deckungspruefung liest — alles aus dem Zustand der Ansicht. */
export interface UncoveredPriorBillingInput {
    /** Die Projektzeilen des Anlagedialogs (buildPickerProjectRows). */
    projectRows: readonly { project: PickerProjectLike }[];
    /** Modell der ausgestellten Belege je Projekt (establishedBillingModeByProject). */
    priorBillingMode: ReadonlyMap<string, BillingMode>;
    /** Belege MIT Positionen; null heisst: noch nicht geladen. */
    pickerInvoices: readonly Invoice[] | null;
    /** Externe Alt-Abschlaege je Projekt; null heisst: nicht ermittelt. */
    externalAdvances: ReadonlyMap<string, readonly ExternalAdvance[]> | null;
    docType: CreatableDocumentType;
    periodTo: string;
    includeUndated: boolean;
}

/**
 * Ungedeckte Altfakturierung je Projekt — uncoveredPriorBillingByProject in
 * InvoicesView (dort steht, wozu der Befund gebraucht wird).
 *
 * Gerechnet wird mit projectUncoveredPriorBilling, also mit DERSELBEN
 * Funktion, die den fertigen Beleg beim Ausstellen sperrt. Nur so kann der
 * Zwang nicht greifen, waehrend die Sperre schon zufrieden waere.
 */
export const uncoveredPriorBillingForRows = ({
    projectRows,
    priorBillingMode,
    pickerInvoices,
    externalAdvances,
    docType,
    periodTo,
    includeUndated,
}: UncoveredPriorBillingInput): Map<string, UncoveredPriorBilling> => {
    const out = new Map<string, UncoveredPriorBilling>();
    // Nur wo der Zwang ueberhaupt etwas aendern kann: eine Belegart, die
    // das Modell gar nicht liest (Abschlag), braucht ihn nicht, und ohne
    // die Alt-Abschlaege duerfte gar nicht gezwungen werden. Die Rechnung
    // laeuft sonst ueber JEDES gelistete Projekt — bei zweihundert
    // Projekten ist das spuerbar.
    //
    // Der frueher hier stehende Haken `cumulative` ist bewusst RAUS: der
    // Befund entscheidet jetzt, ob die Frage ueberhaupt gestellt wird, und
    // wer erst nach der Antwort rechnet, fragt bei den rund 150
    // Bestandsprojekten nach etwas, das nicht waehlbar ist. Nachgeladen
    // werden muss dafuer nichts — priorBillingMode, pickerInvoices und
    // externalAdvances stehen ohnehin unabhaengig vom Dialogzustand.
    if (!consumesBillingMode(docType) || externalAdvances === null) return out;
    for (const row of projectRows) {
        const p = row.project;
        // Das etablierte Projektmodell schlaegt den Zwang (Rangfolge in
        // BillingModeReason) — wo es steht, braucht er nicht gerechnet zu
        // werden.
        if (priorBillingMode.get(p.id)) continue;
        const found = projectUncoveredPriorBilling({
            services: getNormalizedServices(p),
            serviceLog: getServiceLog(p.data),
            // Dieselbe Mengenquelle, aus der buildProjectServiceLines
            // previouslyInvoicedQty bildet — die Vorbelege GENAU dieses
            // Projekts, vorzeichenrichtig.
            invoicedQtyByService: invoicedQuantityByService(
                (pickerInvoices ?? []).filter((i) => i.primaryProjectId === p.id),
            ),
            // Leere Liste heisst „nachgeschlagen, keine erfasst" und ist
            // etwas anderes als das null oben ("nicht ermittelt").
            externalAdvances: externalAdvances.get(p.id) ?? [],
            asOf: periodTo || undefined,
            includeUndated,
        });
        if (found) out.set(p.id, found);
    }
    return out;
};

/** Ladezustand der Deckungspruefung — coverageLoad in InvoicesView. */
export type CoverageLoad = 'loading' | 'ready' | 'failed';

/** Was der Zustand der Abrechnungsart je Projekt liest. */
export interface BillingStancesInput {
    projectRows: readonly { project: { id: string } }[];
    priorBillingMode: ReadonlyMap<string, BillingMode>;
    uncoveredPriorBillingByProject: ReadonlyMap<string, UncoveredPriorBilling>;
    coverageLoad: CoverageLoad;
}

/**
 * Der Zustand jedes gelisteten Projekts — billingStanceByProject in
 * InvoicesView. Die Regel selbst steht in billingMode
 * (billingStancesByProject); hier nur, welcher Ladezustand als bekannt gilt.
 */
export const billingStancesForRows = ({
    projectRows,
    priorBillingMode,
    uncoveredPriorBillingByProject,
    coverageLoad,
}: BillingStancesInput): Map<string, BillingStance> =>
    billingStancesByProject(
        projectRows.map((r) => r.project.id),
        priorBillingMode,
        uncoveredPriorBillingByProject,
        // „laeuft noch" ist keine Aussage: solange die Deckungspruefung
        // unterwegs ist, steht der Zustand auf 'checking' statt auf
        // 'open'. Ein FEHLGESCHLAGENER Lauf zaehlt dagegen als bekannt
        // und faellt auf die bisherige Regel zurueck — ein Fehler darf
        // keine Abrechnungsart erzwingen.
        coverageLoad !== 'loading',
    );

// ─── Liste, Suche und Summe ─────────────────────────────────────────────────

/** Was der Betrag an einer Zeile liest. */
export interface PickerAmountRow {
    netCents: Cents;
    contractedNetCents: Cents;
}

/** Was Grundlage, Betragsfilter und Suche an einer Zeile lesen. */
export interface PickerListRow extends PickerAmountRow {
    positions: readonly unknown[];
    project: { name?: string | null; project_number?: string | null };
}

/** Zeilen mit mindestens einer offenen Position — billableProjects in InvoicesView. */
export const billableRows = <R extends { positions: readonly unknown[] }>(
    rows: readonly R[],
): R[] => rows.filter((x) => x.positions.length > 0);

/**
 * Der Betrag, auf den es bei DIESER Belegart ankommt.
 *
 * Beim Abschlag ist das der Auftragswert — er ist die Bemessungsgrundlage,
 * das Offene spielt dort keine Rolle. Sonst der offene Betrag.
 */
export const relevantNetCentsFor = (
    row: PickerAmountRow,
    docType: CreatableDocumentType,
): number => (docType === 'ADVANCE_INVOICE' ? row.contractedNetCents : row.netCents);

/** Was die Ansicht fuer die angebotenen Projekte beisteuert. */
export interface PickerProjectsInput<R extends PickerListRow> {
    /** Alle Projektzeilen (buildPickerProjectRows). */
    projectRows: readonly R[];
    /** Davon die mit offener Position (billableRows). */
    billableProjects: readonly R[];
    docType: CreatableDocumentType;
    /** Suchtext der Projektliste, wie getippt. */
    projectSearch: string;
}

/**
 * Die Projekte, die der Anlagedialog anbietet — pickerProjects in
 * InvoicesView. Reihenfolge wie in den Zeilen; es wird nur weggelassen.
 */
export const filterPickerProjects = <R extends PickerListRow>({
    projectRows,
    billableProjects,
    docType,
    projectSearch,
}: PickerProjectsInput<R>): R[] => {
    const relevantNetCents = (r: R): number => relevantNetCentsFor(r, docType);

    // Der Abschlag braucht keine OFFENE Menge: er fordert vor der Leistung und
    // rechnet ueber den Auftragswert.
    //
    // Der frueher hier stehende zweite Zweig fuer kumulative Rechnungen ist
    // ersatzlos entfallen, weil er beweisbar nichts tat: `netCents` ist die
    // Summe ueber `positions`, ohne Position also 0 — und der Betragsfilter
    // unten (`relevantNetCents(r) > 0`, fuer INVOICE eben `netCents`) warf
    // genau die Zeilen wieder heraus, die `projectRows` gegenueber
    // `billableProjects` mehr enthaelt. Beide Zweige lieferten fuer INVOICE
    // dieselbe Liste.
    const base = docType === 'ADVANCE_INVOICE' ? projectRows : billableProjects;

    // Ohne Betrag gibt es nichts zu berechnen — auch kumulativ nicht: dort
    // ist der offene Betrag genau das, was der Beleg fordern wuerde.
    // Bei zweihundert Projekten ist jede Nullzeile eine, die man lesen und
    // wieder verwerfen muss.
    const withAmount = base.filter((r) => relevantNetCents(r) > 0);

    const needle = projectSearch.trim().toLowerCase();
    if (!needle) return withAmount;
    return withAmount.filter((r) =>
        `${r.project.name ?? ''} ${r.project.project_number ?? ''}`
            .toLowerCase()
            .includes(needle),
    );
};

/** Summe der fuer die Belegart relevanten Betraege — pickerTotalNetCents in InvoicesView. */
export const totalRelevantNetCents = (
    rows: readonly PickerAmountRow[],
    docType: CreatableDocumentType,
): number => rows.reduce((sum, r) => sum + relevantNetCentsFor(r, docType), 0);
